import { ShopInfo_shop_permissions } from "@saleor/components/Shop/types/ShopInfo";
import { User } from "@saleor/fragments/types/User";
import difference from "lodash-es/difference";

import { PermissionGroupDetailsPageFormData } from "../components/PermissionGroupDetailsPage";
import { PermissionGroupDetails_permissionGroup } from "../types/PermissionGroupDetails";
import {
  ActivityLogsNewValues,
  isActivityLogsNewValues
} from "./activityLogsNewValues";
import {
  ActivityLogsOldValues,
  isActivityLogsOldValues
} from "./activityLogsOldValues";

/**
 * Will return true if group has all permissions available in shop assigned.
 */
export const isGroupFullAccess = (
  permissionGroup: PermissionGroupDetails_permissionGroup,
  shopPermissions: ShopInfo_shop_permissions[]
) => {
  const assignedCodes = extractPermissionCodes(permissionGroup);

  if (assignedCodes.length !== shopPermissions?.length) {
    return false;
  }

  for (const permission of shopPermissions) {
    if (assignedCodes.indexOf(permission.code) === undefined) {
      return false;
    }
  }
  return true;
};

/**
 * Return list of codes which are assigned to the permission group.
 */
export const extractPermissionCodes = (
  permissionGroup: PermissionGroupDetails_permissionGroup
) =>
  permissionGroup?.permissions
    ? permissionGroup.permissions.map(perm => perm.code)
    : [];

/**
 * Return lists of permissions which have to be added and removed from group.
 */
export const permissionsDiff = (
  permissionGroup: PermissionGroupDetails_permissionGroup,
  formData: PermissionGroupDetailsPageFormData
) => {
  const newPermissions = formData.permissions;
  const oldPermissions = extractPermissionCodes(permissionGroup);

  return {
    addPermissions: difference(newPermissions, oldPermissions),
    removePermissions: difference(oldPermissions, newPermissions)
  };
};

/**
 * Return lists of users which have to be added and removed from group.
 */
export const usersDiff = (
  permissionGroup: PermissionGroupDetails_permissionGroup,
  formData: PermissionGroupDetailsPageFormData
) => {
  const newUsers = formData.users.map(u => u.id);
  const oldUsers = permissionGroup?.users.map(u => u.id);

  return {
    addUsers: difference(newUsers, oldUsers),
    removeUsers: difference(oldUsers, newUsers)
  };
};

/**
 * Permissions are exceeded when group has permission which is not handled by user
 */
export const arePermissionsExceeded = (
  permissionGroup: PermissionGroupDetails_permissionGroup,
  user: User
) => {
  const groupPermissions = extractPermissionCodes(permissionGroup);
  const userPermissions = user.userPermissions.map(p => p.code);
  return difference(groupPermissions, userPermissions).length > 0;
};

export enum Coupons_SubPermission_Enums {
  FEATURE_COUPONS = "FEATURE_COUPONS",
  ADD_COUPON = "ADD_COUPON",
  CHANGE_COUPON = "CHANGE_COUPON",
  VIEW_COUPON_REDEMPTION = "VIEW_COUPON_REDEMPTION",
  MANAGE_COUPON_USAGE_LIMIT = "MANAGE_COUPON_USAGE_LIMIT",
  MANAGE_COUPON_USAGE_LIMIT_EVENTS = "MANAGE_COUPON_USAGE_LIMIT_EVENTS"
}

export const defaultCouponSubpermissions: string[] = [
  Coupons_SubPermission_Enums.FEATURE_COUPONS,
  Coupons_SubPermission_Enums.ADD_COUPON,
  Coupons_SubPermission_Enums.CHANGE_COUPON,
  Coupons_SubPermission_Enums.VIEW_COUPON_REDEMPTION,
  Coupons_SubPermission_Enums.MANAGE_COUPON_USAGE_LIMIT
];

export const removeSubpermissions = (permissionArray, subpermissionsArray) =>
  permissionArray.filter(perm => !subpermissionsArray.includes(perm.code));

export const handleSubpermissionsArrayToggle = (
  str,
  subpermission,
  userPermissions
) => {
  const enumArray = subpermission;
  if (str === "remove") {
    return userPermissions.filter(perm => !enumArray.includes(perm));
  } else if (str === "add") {
    const uniqueValue = subpermission.filter(
      perm => !userPermissions.includes(perm)
    );
    return [...userPermissions, ...uniqueValue];
  }
};

export const parseNewValues = (raw: string): ActivityLogsNewValues => {
  let parsed: any = raw;

  try {
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (isActivityLogsNewValues(parsed)) {
      return parsed[0].fields;
    }

    return parsed[0].fields;
  } catch (err) {
    console.error("Failed to parse newValues:", err);
    throw err;
  }
};

export const parseOldValues = (raw: string): ActivityLogsOldValues => {
  let parsed: any = raw;

  try {
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (isActivityLogsOldValues(parsed)) {
      return parsed[0].fields;
    }

    return parsed[0].fields;
  } catch (err) {
    console.error("Failed to parse newValues:", err);
    throw err;
  }
};
